import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { FontSelectorProps } from '../../types/calendar';

export function FontSelector({ fontOptions, selectedFont, onFontSelect }: FontSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedFontOption = fontOptions.find(f => f.family === selectedFont) || fontOptions[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (fontFamily: string) => {
    onFontSelect(fontFamily);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-2">Select Font</label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <span style={{ fontFamily: selectedFontOption.family }} className="text-base">
          {selectedFontOption.name}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {fontOptions.map((font) => (
            <button
              key={font.id}
              type="button"
              onClick={() => handleSelect(font.family)}
              className={`w-full px-3 py-2.5 text-left hover:bg-blue-50 transition-colors ${
                font.family === selectedFont ? 'bg-blue-100' : ''
              }`}
              style={{ fontFamily: font.family }}
            >
              <span className="text-base">{font.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
